import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import type { Membership } from '../api/client';
import { Icon, type IconName } from '../lib/icons';
import { useUnread, useUnreadParts } from '../lib/messages';
import { useUnheardVoicemail } from '../lib/voicemail';
import { YouPicture } from './PhotoEditor';
import { RecordingChip } from './SessionRecording';
import { PHONE_QUERY } from '../lib/portal';
import { initials } from '../lib/session';
import { HelpButton } from './Help';

/**
 * The Stitch app on a phone, for the people in a company (Slack's shape, in Stitch's look): the
 * company across the top with search and you; Home, DMs, Activity, Calls and More in a bar at the
 * bottom. It's for talking and calls; running the phone system is under More → Company admin, and
 * easier on a computer. Stitch staff get the portal's own menu instead (Shell's PhoneChrome).
 */

/** A phone-size screen: the phone app's layout. */
export function usePhone(): boolean {
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && window.matchMedia(PHONE_QUERY).matches);
  useEffect(() => {
    const m = window.matchMedia(PHONE_QUERY);
    const on = () => setPhone(m.matches);
    on();
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return phone;
}

/** A phone app page's title (the tab's name), with something to do on the right. */
export function PhonePageHead({ title, action, hidden }: { title: string; action?: ReactNode; hidden?: boolean }) {
  useEffect(() => {
    document.title = `${title} · Stitch`;
  }, [title]);
  if (hidden) return <h1 className="sr-only">{title}</h1>;
  // A Help button on each page, as on a computer (2987): the guide for the screen you're on.
  return (
    <div className="ppage-head">
      <h1>{title}</h1>
      <span className="ppage-head-actions">
        {action}
        <HelpButton compact />
      </span>
    </div>
  );
}

/** What the phone app's More page needs from the layout around it. */
export interface PhoneShell {
  base: string;
  company: string;
  /** "Ext 201", "Admin", "Messaging only". */
  subtitle: string;
  membership: Membership | undefined;
  admin: boolean;
  /** Every page of the company's admin (the sidebar's menu), for More → Company admin. */
  adminMenu: ReactNode | null;
  switchCompany: boolean;
  openSettings: () => void;
  signOut: () => void;
}
export const PhoneShellContext = createContext<PhoneShell | null>(null);
export const usePhoneShell = () => useContext(PhoneShellContext);

interface Tab {
  to: string;
  icon: IconName;
  label: string;
  /** A number, or just a dot for "something new". */
  badge?: number | 'dot';
  here: (rest: string) => boolean;
}

/** The badge on a tab's icon (seen only: its words follow the tab's name, so "Home" stays first). */
function Badge({ badge }: { badge: Tab['badge'] }) {
  if (!badge) return null;
  if (badge === 'dot') return <span className="papp-dot" aria-hidden="true" />;
  return (
    <span className="papp-badge" aria-hidden="true">
      {badge > 99 ? '99+' : badge}
    </span>
  );
}

/** What a badge says to a screen reader, after the tab's name: "Home, unread messages". */
function badgeWords(badge: Tab['badge'], label: string): string | null {
  if (!badge) return null;
  const what = label === 'Activity' ? 'mentions' : label === 'Home' ? 'unread messages' : label === 'Calls' ? 'new voicemail' : 'unread';
  return badge === 'dot' ? `, ${what}` : `, ${badge > 99 ? '99+' : badge} ${what}`;
}

export function PhoneApp({
  base,
  domain,
  company,
  myId,
  myName,
  phone,
  switchCompany
}: {
  base: string;
  domain: string;
  company: string;
  myId: string | undefined;
  myName: string;
  phone: boolean;
  switchCompany: boolean;
}) {
  const { pathname } = useLocation();
  const total = useUnread(domain, myId, true);
  const parts = useUnreadParts(domain, myId, true);
  const unheard = useUnheardVoicemail(domain);
  const rest = pathname.slice(base.length) || '/';
  const tabs: Tab[] = [
    {
      to: `${base}/messages`,
      icon: 'home',
      label: 'Home',
      badge: total ? 'dot' : undefined,
      here: (r) => r === '/' || r === '/messages' || r.startsWith('/messages/')
    },
    {
      to: `${base}/dms`,
      icon: 'chat',
      label: 'DMs',
      badge: parts?.dms || undefined,
      here: (r) => r === '/dms'
    },
    {
      to: `${base}/feed`,
      icon: 'bell',
      label: 'Activity',
      badge: parts?.mentions || undefined,
      here: (r) => r === '/feed'
    },
    ...(phone
      ? [
          {
            to: `${base}/me/calls`,
            icon: 'phone' as const,
            label: 'Calls',
            badge: unheard || undefined,
            here: (r: string) => r === '/me/calls'
          }
        ]
      : []),
    // Everything else (your phone, the company's pages, settings) is under More.
    { to: `${base}/more`, icon: 'dots', label: 'More', here: () => true }
  ];
  const active = tabs.findIndex((t) => t.here(rest));
  return (
    <>
      <header className="papp-top">
        {switchCompany ? (
          <Link to="/choose" className="papp-co" aria-label={`${company}. Switch company`}>
            <span className="papp-mark" aria-hidden="true">
              {initials(company)}
            </span>
            <span className="papp-co-name">{company}</span>
            <Icon name="chev" size={16} strokeWidth={2.4} />
          </Link>
        ) : (
          <Link to={`${base}/messages`} className="papp-co" aria-label={`${company} home`}>
            <span className="papp-mark" aria-hidden="true">
              {initials(company)}
            </span>
            <span className="papp-co-name">{company}</span>
          </Link>
        )}
        <span className="papp-top-actions">
          <RecordingChip place="top" />
          <Link to={`${base}/messages?search=1`} className="papp-top-btn" aria-label="Search messages">
            <Icon name="search" size={19} strokeWidth={2.1} />
          </Link>
          <Link to={`${base}/more`} className="papp-you" aria-label={`You: ${myName}. Your status, settings and more`}>
            <YouPicture name={myName} />
          </Link>
        </span>
      </header>
      <nav className="papp-tabs" aria-label="Main pages">
        {tabs.map((t, i) => (
          <Link key={t.to} to={t.to} className={`papp-tab${i === active ? ' active' : ''}`} aria-current={i === active ? 'page' : undefined}>
            <span className="papp-tab-icon">
              <Icon name={t.icon} size={22} strokeWidth={i === active ? 2.2 : 1.8} />
              <Badge badge={t.badge} />
            </span>
            <span className="papp-tab-label">{t.label}</span>
            {badgeWords(t.badge, t.label) && <span className="sr-only">{badgeWords(t.badge, t.label)}</span>}
          </Link>
        ))}
      </nav>
    </>
  );
}
